import React, { useEffect, useRef, useState } from "react";
import { getR2Config, saveR2Config } from "../../services/r2";
import { getEpointConfig, saveEpointConfig } from "../../services/firestore";
import { getAIChatConfig, saveAIChatConfig, AI_CHAT_DEFAULTS } from "../../services/aiChat";
import { uploadImageToR2 } from "../../services/r2";
import CircularImageCropper from "../../components/CircularImageCropper";
import { Save, Plus, Trash2, Check, Lock, CreditCard, Copy, Eye, EyeOff, Sparkles, Upload, Image as ImageIcon, X } from "lucide-react";

const EMPTY_BUCKET = { name: "", access_key: "", secret_key: "", public_url: "", active: false };
const SESSION_KEY = "najafli_said_unlocked";
const ACCESS_CODE = "2325";

export default function AdminBuckets() {
  const [unlocked, setUnlocked] = useState(() => sessionStorage.getItem(SESSION_KEY) === "1");
  const [codeInput, setCodeInput] = useState("");
  const [codeError, setCodeError] = useState("");
  const [cfg, setCfg] = useState({ account_id: "", buckets: [] });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  // ---- Epoint.az payment-gateway config ----
  const [epoint, setEpoint] = useState({
    public_key: "",
    private_key: "",
    site_url: "",
    success_url: "",
    error_url: "",
    result_url: "",
    redirect_base: "",
    enabled: false,
  });
  const [showPrivate, setShowPrivate] = useState(false);
  const [epointBusy, setEpointBusy] = useState(false);
  const [epointMsg, setEpointMsg] = useState("");

  // ---- AI Chat (Gemini) config ----
  const [ai, setAi] = useState({
    api_key: "",
    model: "gemini-2.5-flash",
    system_prompt: "",
    welcome: "",
    enabled: true,
    // UI strings
    header_title: "",
    header_subtitle: "",
    tooltip_text: "",
    input_placeholder: "",
    typing_text: "",
    quick_prompts_label: "",
    clear_button_text: "",
    fallback_error_text: "",
    launcher_label: "V",
    suggested_prompts: [],
    logo_url: "",
  });
  const [showAiKey, setShowAiKey] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiMsg, setAiMsg] = useState("");
  const [aiTesting, setAiTesting] = useState(false);
  const [aiTestResult, setAiTestResult] = useState("");
  // logo upload + crop state
  const [cropperOpen, setCropperOpen] = useState(false);
  const [cropperFile, setCropperFile] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const logoFileInputRef = useRef(null);

  useEffect(() => {
    if (!unlocked) return;
    getR2Config().then((c) => { if (c) setCfg({ account_id: c.account_id || "", buckets: c.buckets || [] }); });
    getEpointConfig().then((e) => {
      // Always default to the production domain so the merchant cabinet
      // gets the correct URLs even if the admin opens this page from a
      // staging/preview deployment.
      const SITE = "https://vinylbaku.az";
      setEpoint((prev) => ({
        ...prev,
        public_key: e?.public_key || "",
        private_key: e?.private_key || "",
        site_url: e?.site_url || SITE,
        success_url: e?.success_url || `${SITE}/success`,
        error_url: e?.error_url || `${SITE}/error`,
        result_url: e?.result_url || `${SITE}/result`,
        redirect_base: e?.redirect_base || "",
        enabled: !!e?.enabled,
      }));
    });
    getAIChatConfig().then((c) => {
      setAi({
        api_key: c.api_key || "",
        model: c.model || "gemini-2.5-flash",
        system_prompt: c.system_prompt || "",
        welcome: c.welcome || "",
        enabled: c.enabled !== false,
        header_title: c.header_title || "",
        header_subtitle: c.header_subtitle || "",
        tooltip_text: c.tooltip_text || "",
        input_placeholder: c.input_placeholder || "",
        typing_text: c.typing_text || "",
        quick_prompts_label: c.quick_prompts_label || "",
        clear_button_text: c.clear_button_text || "",
        fallback_error_text: c.fallback_error_text || "",
        launcher_label: c.launcher_label || "V",
        suggested_prompts: Array.isArray(c.suggested_prompts) ? c.suggested_prompts : [],
        logo_url: c.logo_url || "",
      });
    });
  }, [unlocked]);

  const tryUnlock = (e) => {
    e.preventDefault();
    if (codeInput.trim() === ACCESS_CODE) {
      sessionStorage.setItem(SESSION_KEY, "1");
      setUnlocked(true);
      setCodeError("");
      setCodeInput("");
    } else {
      setCodeError("Kod yanlışdır.");
      setCodeInput("");
    }
  };

  const addBucket = () => setCfg((c) => ({ ...c, buckets: [...c.buckets, { ...EMPTY_BUCKET, id: "b" + Date.now(), active: c.buckets.length === 0 }] }));
  const removeBucket = (idx) => setCfg((c) => ({ ...c, buckets: c.buckets.filter((_, i) => i !== idx) }));
  const updateBucket = (idx, patch) => setCfg((c) => ({ ...c, buckets: c.buckets.map((b, i) => i === idx ? { ...b, ...patch } : b) }));
  const setActive = (idx) => setCfg((c) => ({ ...c, buckets: c.buckets.map((b, i) => ({ ...b, active: i === idx })) }));

  const save = async () => {
    setBusy(true); setMsg("");
    try {
      // ensure exactly one active
      let active = cfg.buckets.findIndex((b) => b.active);
      if (active < 0 && cfg.buckets.length) active = 0;
      const buckets = cfg.buckets.map((b, i) => ({ ...b, active: i === active }));
      await saveR2Config({ account_id: cfg.account_id.trim(), buckets });
      setMsg("Yadda saxlandı. ✓");
    } catch (e) { setMsg(e.message || "Xəta."); }
    finally { setBusy(false); setTimeout(() => setMsg(""), 4000); }
  };

  const updateEpoint = (patch) => setEpoint((p) => ({ ...p, ...patch }));

  const saveEpoint = async () => {
    setEpointBusy(true); setEpointMsg("");
    try {
      await saveEpointConfig({
        public_key: epoint.public_key.trim(),
        private_key: epoint.private_key.trim(),
        site_url: epoint.site_url.trim(),
        success_url: epoint.success_url.trim(),
        error_url: epoint.error_url.trim(),
        result_url: epoint.result_url.trim(),
        redirect_base: (epoint.redirect_base || "").trim(),
        enabled: !!epoint.enabled,
      });
      setEpointMsg("Epoint konfiqurasiyası yadda saxlandı. ✓");
    } catch (e) { setEpointMsg(e.message || "Xəta."); }
    finally { setEpointBusy(false); setTimeout(() => setEpointMsg(""), 4000); }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setEpointMsg("Kopyalandı ✓");
      setTimeout(() => setEpointMsg(""), 1800);
    } catch { /* ignore */ }
  };

  const saveAi = async () => {
    setAiBusy(true); setAiMsg("");
    try {
      await saveAIChatConfig({
        api_key: ai.api_key.trim(),
        model: (ai.model || "gemini-2.5-flash").trim(),
        system_prompt: ai.system_prompt.trim(),
        welcome: ai.welcome.trim(),
        enabled: !!ai.enabled,
        header_title: ai.header_title.trim(),
        header_subtitle: ai.header_subtitle.trim(),
        tooltip_text: ai.tooltip_text.trim(),
        input_placeholder: ai.input_placeholder.trim(),
        typing_text: ai.typing_text.trim(),
        quick_prompts_label: ai.quick_prompts_label.trim(),
        clear_button_text: ai.clear_button_text.trim(),
        fallback_error_text: ai.fallback_error_text.trim(),
        launcher_label: (ai.launcher_label || "V").trim().charAt(0).toUpperCase() || "V",
        suggested_prompts: (ai.suggested_prompts || [])
          .map((s) => (typeof s === "string" ? s.trim() : ""))
          .filter(Boolean)
          .slice(0, 8), // sanity cap
        logo_url: (ai.logo_url || "").trim(),
      });
      setAiMsg("AI konfiqurasiyası yadda saxlandı. ✓");
    } catch (e) { setAiMsg(e.message || "Xəta."); }
    finally { setAiBusy(false); setTimeout(() => setAiMsg(""), 4000); }
  };

  // ─── Suggested-prompts array helpers ─────────────────────────────────
  const addPrompt = () => setAi((p) => ({
    ...p,
    suggested_prompts: [...(p.suggested_prompts || []), ""],
  }));
  const updatePrompt = (idx, val) => setAi((p) => ({
    ...p,
    suggested_prompts: (p.suggested_prompts || []).map((s, i) => (i === idx ? val : s)),
  }));
  const removePrompt = (idx) => setAi((p) => ({
    ...p,
    suggested_prompts: (p.suggested_prompts || []).filter((_, i) => i !== idx),
  }));

  // ─── Logo upload + circular crop flow ────────────────────────────────
  const onLogoFilePicked = (e) => {
    const f = e.target.files?.[0];
    e.target.value = ""; // allow picking the same file twice
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      setAiMsg("Yalnız şəkil faylı seçin.");
      setTimeout(() => setAiMsg(""), 3000);
      return;
    }
    setCropperFile(f);
    setCropperOpen(true);
  };

  const onCropConfirm = async (blob /*, dataUrl */) => {
    setCropperOpen(false);
    setCropperFile(null);
    if (!blob) return;
    setLogoUploading(true);
    setAiMsg("");
    try {
      // Convert Blob to File so the R2 helper can name it deterministically.
      const file = new File([blob], `ai-logo-${Date.now()}.png`, { type: "image/png" });
      const url = await uploadImageToR2(file, { folder: "ai-chat" });
      setAi((p) => ({ ...p, logo_url: url }));
      setAiMsg("Logo yükləndi — dəyişiklikləri yadda saxlayın.");
    } catch (err) {
      console.error("[AdminBuckets] logo upload failed", err);
      setAiMsg("Logo yüklənmədi: " + (err?.message || "naməlum xəta"));
    } finally {
      setLogoUploading(false);
      setTimeout(() => setAiMsg(""), 5000);
    }
  };
  const onCropCancel = () => { setCropperOpen(false); setCropperFile(null); };
  const clearLogo = () => setAi((p) => ({ ...p, logo_url: "" }));

  const testAi = async () => {
    setAiTesting(true); setAiTestResult("");
    try {
      const { sendGeminiMessage } = await import("../../services/aiChat");
      const reply = await sendGeminiMessage({
        apiKey: ai.api_key.trim(),
        model: (ai.model || "gemini-2.5-flash").trim(),
        systemInstruction: ai.system_prompt.trim() || "Sən Azərbaycan dilində danışan köməkçisən.",
        history: [],
        userText: "Salam, sən kimsən?",
      });
      setAiTestResult("✓ Cavab: " + reply.slice(0, 220));
    } catch (e) {
      setAiTestResult("✗ Xəta: " + (e.message || "Test uğursuz oldu."));
    } finally {
      setAiTesting(false);
    }
  };

  return (
    <div className="p-10" data-testid="admin-buckets">
      {!unlocked && (
        <div className="max-w-md mx-auto mt-20" data-testid="najafli-lock-screen">
          <div className="flex flex-col items-center text-center">
            <div className="w-16 h-16 rounded-full bg-[var(--surface)] border border-[var(--border)] flex items-center justify-center mb-6">
              <Lock size={24} className="text-[var(--text-muted)]" />
            </div>
            <div className="metadata mb-3">MƏXFİ BÖLMƏ</div>
            <h1 className="font-display text-3xl mb-3">Najafli Said</h1>
            <p className="text-[var(--text-2)] text-sm leading-relaxed mb-8 max-w-sm">
              Bu bölmə şifrə ilə qorunur. Davam etmək üçün giriş kodunu daxil edin.
            </p>
            <form onSubmit={tryUnlock} className="w-full space-y-4">
              <input
                type="password"
                value={codeInput}
                onChange={(e) => { setCodeInput(e.target.value); setCodeError(""); }}
                placeholder="• • • •"
                inputMode="numeric"
                autoFocus
                className="w-full text-center tracking-[0.6em] font-mono text-2xl bg-transparent border border-[var(--border)] px-4 py-4 focus:border-[var(--primary)] outline-none transition-colors"
                data-testid="najafli-code-input"
              />
              {codeError && (
                <div className="metadata text-[var(--error)] text-center" data-testid="najafli-code-error">
                  {codeError}
                </div>
              )}
              <button type="submit" className="btn-primary w-full justify-center" data-testid="najafli-unlock-btn">
                <Lock size={13} /> AÇMAQ
              </button>
            </form>
          </div>
        </div>
      )}

      {unlocked && (
      <>
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="metadata">MƏXFİ · SAXLAMA</div>
          <h1 className="font-display text-5xl mt-1">Najafli Said</h1>
          <p className="text-[var(--text-2)] text-2 text-sm mt-3 max-w-2xl leading-relaxed">
            Şəkillər aktiv saxlama yerinə yüklənir. Saxlama yeri dolduqda yenisini əlavə edib aktiv olaraq işarələyin — köhnə saxlama yerindəki şəkillərin URL-ləri saytda işləməyə davam edir.
          </p>
        </div>
        <button onClick={save} disabled={busy} className="btn-primary" data-testid="bucket-save"><Save size={14}/> {busy ? "…" : "YADDA SAXLA"}</button>
      </div>
      {msg && <div className="mb-4 text-sm text-[var(--primary)]">{msg}</div>}

      <div className="mb-8">
        <label className="metadata">CLOUDFLARE HESAB ID</label>
        <input className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1 font-mono" value={cfg.account_id} onChange={(e) => setCfg({ ...cfg, account_id: e.target.value })} placeholder="məs: 1a2b3c4d5e6f7g8h" data-testid="r2-account-id"/>
      </div>

      <div className="flex items-end justify-between mb-3">
        <div className="metadata">SAXLAMA YERLƏRİ</div>
        <button onClick={addBucket} className="btn-outline" data-testid="r2-add-bucket"><Plus size={14}/> SAXLAMA YERİ ƏLAVƏ ET</button>
      </div>

      <div className="space-y-4">
        {cfg.buckets.map((b, i) => (
          <div key={b.id || i} className={`border ${b.active ? "border-[var(--primary)]" : "border-[var(--border)]"} p-5`} data-testid={`bucket-${i}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="font-display text-2xl flex items-center gap-3">
                {b.name || `Saxlama #${i+1}`}
                {b.active && <span className="metadata text-[var(--primary)] flex items-center gap-1"><Check size={14}/> AKTİV</span>}
              </div>
              <div className="flex gap-2">
                {!b.active && <button onClick={() => setActive(i)} className="metadata link-underline text-[var(--primary)]" data-testid={`set-active-${i}`}>AKTİV ET</button>}
                <button onClick={() => removeBucket(i)} className="p-1 text-[var(--accent)]" data-testid={`remove-bucket-${i}`}><Trash2 size={14}/></button>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div><label className="metadata">SAXLAMA ADI</label><input className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1 font-mono text-sm" value={b.name} onChange={(e) => updateBucket(i, { name: e.target.value })} placeholder="vinyl-baku-1"/></div>
              <div><label className="metadata">İCTİMAİ URL (R2.dev)</label><input className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1 font-mono text-sm" value={b.public_url} onChange={(e) => updateBucket(i, { public_url: e.target.value })} placeholder="https://pub-xxx.r2.dev"/></div>
              <div><label className="metadata">GİRİŞ AÇARI ID</label><input className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1 font-mono text-sm" value={b.access_key} onChange={(e) => updateBucket(i, { access_key: e.target.value })}/></div>
              <div><label className="metadata">MƏXFİ GİRİŞ AÇARI</label><input type="password" className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1 font-mono text-sm" value={b.secret_key} onChange={(e) => updateBucket(i, { secret_key: e.target.value })}/></div>
            </div>
          </div>
        ))}
        {!cfg.buckets.length && <div className="p-10 text-center text-[var(--text-muted)] metadata border border-[var(--border)]">HEÇ SAXLAMA YERİ YOXDUR — ƏLAVƏ ET</div>}
      </div>

      <div className="mt-10 p-5 border border-[var(--border-soft)] text-sm text-[var(--text-2)] leading-relaxed">
        <div className="metadata mb-2">CORS QAYDASI</div>
        <p>R2 saxlama yerinizə brauzerdən yükləmə üçün CORS qaydası lazımdır. Cloudflare İdarə Paneli → R2 → Saxlama → Tənzimləmələr → CORS Qaydası:</p>
<pre className="bg-[var(--surface)] border border-[var(--border)] p-3 mt-2 font-mono text-xs whitespace-pre overflow-x-auto">{`[
  {
    "AllowedOrigins": ["*"],
    "AllowedMethods": ["GET", "PUT", "POST", "DELETE", "HEAD"],
    "AllowedHeaders": ["*"],
    "ExposeHeaders": ["ETag"],
    "MaxAgeSeconds": 3000
  }
]`}</pre>
        <p className="mt-2 text-xs text-[var(--text-muted)]">
          Qeyd: video kimi böyük fayllar üçün <span className="font-mono">POST</span> və <span className="font-mono">DELETE</span> mütləqdir (multipart upload). Yalnız <span className="font-mono">PUT</span>/<span className="font-mono">GET</span> olarsa, video upload xəta verəcək.
        </p>
      </div>

      {/* ====== EPOINT.AZ PAYMENT GATEWAY ====== */}
      <div className="mt-16 pt-10 border-t-2 border-[var(--border)]" data-testid="epoint-section">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
          <div>
            <div className="metadata flex items-center gap-2 text-[var(--accent)]">
              <CreditCard size={12} /> MƏXFİ · ÖDƏNİŞ ŞLÜZÜ
            </div>
            <h2 className="font-display text-4xl mt-2">Epoint.az</h2>
            <p className="text-[var(--text-2)] text-sm mt-3 max-w-2xl leading-relaxed">
              Epoint kabinetindən aldığınız <span className="font-mono">İctimai Açar</span> və {" "}
              <span className="font-mono">Məxfi Açar</span>-ı bura daxil edin. Aşağıdakı URL-ləri isə
              Epoint kabinetində müvafiq sahələrə yapışdırın.
            </p>
          </div>
          <button onClick={saveEpoint} disabled={epointBusy} className="btn-primary" data-testid="epoint-save">
            <Save size={14} /> {epointBusy ? "…" : "EPOINT-İ YADDA SAXLA"}
          </button>
        </div>

        {epointMsg && (
          <div className="mb-4 text-sm text-[var(--primary)]" data-testid="epoint-msg">{epointMsg}</div>
        )}

        <div className="grid md:grid-cols-2 gap-6">
          {/* AÇARLAR */}
          <div className="border border-[var(--border)] p-5">
            <div className="metadata text-[var(--text-muted)] mb-4">AÇARLAR</div>

            <label className="metadata">İCTİMAİ AÇAR</label>
            <input
              type="text"
              className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1 font-mono text-sm"
              value={epoint.public_key}
              onChange={(e) => updateEpoint({ public_key: e.target.value })}
              placeholder="i000000xx"
              data-testid="epoint-public-key"
            />

            <label className="metadata mt-5 block">MƏXFİ AÇAR</label>
            <div className="relative mt-1">
              <input
                type={showPrivate ? "text" : "password"}
                className="w-full bg-transparent border border-[var(--border)] px-3 py-2 pr-10 font-mono text-sm"
                value={epoint.private_key}
                onChange={(e) => updateEpoint({ private_key: e.target.value })}
                placeholder="••••••••••••••••"
                data-testid="epoint-private-key"
              />
              <button
                type="button"
                onClick={() => setShowPrivate((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[var(--text-muted)] hover:text-[var(--primary)]"
                aria-label={showPrivate ? "Gizlət" : "Göstər"}
                data-testid="epoint-private-toggle"
              >
                {showPrivate ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>

            <label className="metadata mt-6 inline-flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={epoint.enabled}
                onChange={(e) => updateEpoint({ enabled: e.target.checked })}
                data-testid="epoint-enabled"
              />
              <span>EPOINT-İ AKTİVLƏŞDİR (CANLI)</span>
            </label>
          </div>

          {/* URLS */}
          <div className="border border-[var(--border)] p-5">
            <div className="metadata text-[var(--text-muted)] mb-4">EPOINT KABİNETİNƏ DAXİL EDİLƏCƏK URL-LƏR</div>

            {[
              { key: "site_url", label: "Veb saytın ünvanı", testid: "epoint-site-url" },
              { key: "success_url", label: "Uğurlu əməliyyat linki", testid: "epoint-success-url" },
              { key: "error_url", label: "Uğursuz əməliyyat linki", testid: "epoint-error-url" },
              { key: "result_url", label: "Nəticəni göndərmək üçün link", testid: "epoint-result-url" },
            ].map(({ key, label, testid }) => (
              <div key={key} className="mb-4 last:mb-0">
                <label className="metadata">{label.toUpperCase()}</label>
                <div className="flex gap-2 mt-1">
                  <input
                    type="text"
                    className="flex-1 bg-transparent border border-[var(--border)] px-3 py-2 font-mono text-xs"
                    value={epoint[key]}
                    onChange={(e) => updateEpoint({ [key]: e.target.value })}
                    data-testid={testid}
                  />
                  <button
                    type="button"
                    onClick={() => copyToClipboard(epoint[key])}
                    className="btn-outline px-3"
                    title="Kopyala"
                    aria-label="Kopyala"
                    data-testid={`${testid}-copy`}
                  >
                    <Copy size={14} />
                  </button>
                </div>
              </div>
            ))}

            <p className="text-xs text-[var(--text-muted)] mt-4 leading-relaxed">
              Bu URL-lər Epoint kabinetindəki «Veb saytın ünvanı / Uğurlu / Uğursuz /
              Nəticəni göndərmək üçün link» sahələrinə uyğun olaraq daxil edilməlidir.
              Defolt olaraq <span className="font-mono">https://vinylbaku.az</span> üzərinə
              qurulub — başqa domendən istifadə edirsinizsə dəyişib yenidən yadda saxlayın.
            </p>
          </div>

          {/* REDIRECT BASE OVERRIDE */}
          <div className="border border-[var(--border)] p-5 mt-4">
            <div className="metadata text-[var(--text-muted)] mb-2">ÖDƏNİŞDƏN SONRA YÖNLƏNDİRMƏ DOMENİ</div>
            <label className="metadata">DOMEN (boş buraxılsa cari saytın domeni avtomatik)</label>
            <input
              type="text"
              className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1 font-mono text-xs"
              value={epoint.redirect_base}
              onChange={(e) => updateEpoint({ redirect_base: e.target.value })}
              placeholder="məs. https://vinylbaku.az  (boş = cari sayt)"
              data-testid="epoint-redirect-base"
            />
            <p className="text-xs text-[var(--text-muted)] mt-3 leading-relaxed">
              Müştəri ödənişdən sonra hansı domenə qayıtsın. <strong>Boş buraxılsa</strong> —
              müştəri ödənişi başladığı domenə qayıdacaq (məsələn, müştəri preview URL-də
              alış-veriş edirsə preview-yə, vinylbaku.az-də isə vinylbaku.az-ə). SSL/hosting
              problemi olan domeni burada təyin etməyin — yoxsa müştəri ödədikdən sonra
              xəta səhifəsi görəcək. Tipik halda <strong>boş saxlayın</strong>.
            </p>
          </div>
        </div>
      </div>

      {/* ====== AI CHAT (GEMINI) ====== */}
      <div className="mt-16 pt-10 border-t-2 border-[var(--border)]" data-testid="ai-chat-section">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
          <div>
            <div className="metadata flex items-center gap-2 text-[var(--primary)]">
              <Sparkles size={12} /> MƏXFİ · AI KÖMƏKÇİ
            </div>
            <h2 className="font-display text-4xl mt-2">Gemini AI</h2>
            <p className="text-[var(--text-2)] text-sm mt-3 max-w-2xl leading-relaxed">
              Saytın sol-aşağı küncündə görünən AI köməkçi <span className="font-mono">Google Gemini</span> API
              ilə işləyir və Azərbaycan dilində cavab verir. API açarınızı{" "}
              <a href="https://aistudio.google.com/apikey" target="_blank" rel="noreferrer" className="underline">aistudio.google.com/apikey</a> səhifəsindən əldə edə bilərsiniz.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={testAi} disabled={aiTesting || !ai.api_key} className="btn-outline" data-testid="ai-test">
              {aiTesting ? "TEST EDİLİR…" : "TEST ET"}
            </button>
            <button onClick={saveAi} disabled={aiBusy} className="btn-primary" data-testid="ai-save">
              <Save size={14} /> {aiBusy ? "…" : "AI-NI YADDA SAXLA"}
            </button>
          </div>
        </div>

        {aiMsg && (
          <div className="mb-4 text-sm text-[var(--primary)]" data-testid="ai-msg">{aiMsg}</div>
        )}
        {aiTestResult && (
          <div className={`mb-4 text-sm leading-relaxed px-3 py-2 border ${aiTestResult.startsWith("✓") ? "border-[var(--primary)] text-[var(--primary)]" : "border-[var(--accent)] text-[var(--accent)]"}`} data-testid="ai-test-result">
            {aiTestResult}
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-6">
          {/* KEYS / MODEL */}
          <div className="border border-[var(--border)] p-5">
            <div className="metadata text-[var(--text-muted)] mb-4">AÇAR VƏ MODEL</div>

            <label className="metadata">GEMINI API AÇARI</label>
            <div className="relative mt-1">
              <input
                type={showAiKey ? "text" : "password"}
                className="w-full bg-transparent border border-[var(--border)] px-3 py-2 pr-10 font-mono text-sm"
                value={ai.api_key}
                onChange={(e) => setAi((p) => ({ ...p, api_key: e.target.value }))}
                placeholder="AIza…"
                data-testid="ai-api-key"
              />
              <button
                type="button"
                onClick={() => setShowAiKey((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[var(--text-muted)] hover:text-[var(--primary)]"
                aria-label={showAiKey ? "Gizlət" : "Göstər"}
                data-testid="ai-api-key-toggle"
              >
                {showAiKey ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>

            <label className="metadata mt-5 block">MODEL</label>
            <select
              className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1 font-mono text-sm"
              value={ai.model}
              onChange={(e) => setAi((p) => ({ ...p, model: e.target.value }))}
              data-testid="ai-model"
            >
              <option value="gemini-2.5-flash">gemini-2.5-flash (sürətli, tövsiyə)</option>
              <option value="gemini-flash-latest">gemini-flash-latest</option>
              <option value="gemini-2.5-pro">gemini-2.5-pro (daha güclü)</option>
              <option value="gemini-pro-latest">gemini-pro-latest</option>
              <option value="gemini-2.0-flash">gemini-2.0-flash</option>
            </select>

            <label className="metadata mt-6 inline-flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={ai.enabled}
                onChange={(e) => setAi((p) => ({ ...p, enabled: e.target.checked }))}
                data-testid="ai-enabled"
              />
              <span>AI KÖMƏKÇİNİ AKTİVLƏŞDİR (SAYTDA GÖSTƏR)</span>
            </label>
          </div>

          {/* TƏLİMATLAR */}
          <div className="border border-[var(--border)] p-5">
            <div className="metadata text-[var(--text-muted)] mb-4">DAVRANIŞ TƏLİMATI</div>

            <label className="metadata">SİSTEM TƏLİMATI (AI-nin necə davranacağı)</label>
            <textarea
              className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1 font-mono text-xs leading-relaxed"
              rows={6}
              value={ai.system_prompt}
              onChange={(e) => setAi((p) => ({ ...p, system_prompt: e.target.value }))}
              placeholder="Sən Vinyl Baku mağazasının köməkçisisən. Hər zaman Azərbaycan dilində danış…"
              data-testid="ai-system-prompt"
            />

            <label className="metadata mt-4 block">XOŞ GƏLDİN MESAJI</label>
            <textarea
              className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1 font-mono text-xs leading-relaxed"
              rows={3}
              value={ai.welcome}
              onChange={(e) => setAi((p) => ({ ...p, welcome: e.target.value }))}
              placeholder="Salam! Mən Vinyl Baku süni intellekt köməkçisiyəm…"
              data-testid="ai-welcome"
            />
          </div>
        </div>

        {/* ─── LOGO + UI TEXTS ─────────────────────────────────────────── */}
        <div className="grid md:grid-cols-2 gap-6 mt-6">
          {/* LOGO */}
          <div className="border border-[var(--border)] p-5" data-testid="ai-logo-section">
            <div className="metadata text-[var(--text-muted)] mb-4 flex items-center gap-2">
              <ImageIcon size={12} /> LOGO ŞƏKİLİ
            </div>

            <div className="flex items-start gap-5">
              {/* Preview circle */}
              <div className="shrink-0">
                <div className="w-28 h-28 rounded-full overflow-hidden bg-zinc-900 flex items-center justify-center border border-[var(--border)] shadow-inner">
                  {ai.logo_url ? (
                    <img
                      src={ai.logo_url}
                      alt="Logo"
                      className="w-full h-full object-cover"
                      data-testid="ai-logo-preview"
                    />
                  ) : (
                    <span
                      className="text-white font-serif text-4xl leading-none"
                      style={{ fontFamily: "'Playfair Display', serif" }}
                    >
                      {(ai.launcher_label || "V").charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="metadata text-center mt-2 text-[var(--text-muted)]">ÖN BAXIŞ</div>
              </div>

              {/* Controls */}
              <div className="flex-1 min-w-0">
                <input
                  ref={logoFileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={onLogoFilePicked}
                  className="hidden"
                  data-testid="ai-logo-file"
                />
                <button
                  type="button"
                  onClick={() => logoFileInputRef.current?.click()}
                  disabled={logoUploading}
                  className="btn-primary"
                  data-testid="ai-logo-upload-btn"
                >
                  <Upload size={13} /> {logoUploading ? "YÜKLƏNİR…" : (ai.logo_url ? "DƏYİŞ" : "YÜKLƏ VƏ KƏS")}
                </button>
                {ai.logo_url && (
                  <button
                    type="button"
                    onClick={clearLogo}
                    className="btn-outline mt-2 ml-0"
                    data-testid="ai-logo-clear-btn"
                  >
                    <X size={13} /> LOGOYU SİL
                  </button>
                )}

                <p className="text-xs text-[var(--text-muted)] mt-3 leading-relaxed">
                  Şəkil seçin, açılan pəncərədə dairəvi sahəyə uyğunlaşdırın
                  (sürükləyin / zoom). Logo AI köməkçi düyməsində, başlığında və
                  hər cavab yanında görünəcək.
                </p>

                <label className="metadata mt-4 block">VƏ YA HƏRF (logo yoxdursa)</label>
                <input
                  type="text"
                  maxLength={2}
                  className="w-20 bg-transparent border border-[var(--border)] px-3 py-2 mt-1 font-mono text-base text-center"
                  value={ai.launcher_label}
                  onChange={(e) => setAi((p) => ({ ...p, launcher_label: e.target.value }))}
                  placeholder="V"
                  data-testid="ai-launcher-label"
                />
              </div>
            </div>
          </div>

          {/* UI STRINGS — quick-edit grid */}
          <div className="border border-[var(--border)] p-5" data-testid="ai-texts-section">
            <div className="metadata text-[var(--text-muted)] mb-4">İNTERFEYS MƏTNLƏRİ</div>

            {[
              { key: "header_title",        label: "BAŞLIQ",                ph: AI_CHAT_DEFAULTS.header_title },
              { key: "header_subtitle",     label: "ALT BAŞLIQ",            ph: AI_CHAT_DEFAULTS.header_subtitle },
              { key: "tooltip_text",        label: "BALONCUQ MƏTNİ",        ph: AI_CHAT_DEFAULTS.tooltip_text },
              { key: "input_placeholder",   label: "İNPUT PLACEHOLDER",     ph: AI_CHAT_DEFAULTS.input_placeholder },
              { key: "typing_text",         label: "“YAZIR…” MƏTNİ",        ph: AI_CHAT_DEFAULTS.typing_text },
              { key: "quick_prompts_label", label: "SUAL CHIPS BAŞLIĞI",    ph: AI_CHAT_DEFAULTS.quick_prompts_label },
              { key: "clear_button_text",   label: "“TƏMİZLƏ” DÜYMƏSİ",     ph: AI_CHAT_DEFAULTS.clear_button_text },
            ].map(({ key, label, ph }) => (
              <div key={key} className="mb-3">
                <label className="metadata">{label}</label>
                <input
                  type="text"
                  className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1 font-mono text-xs"
                  value={ai[key]}
                  onChange={(e) => setAi((p) => ({ ...p, [key]: e.target.value }))}
                  placeholder={ph}
                  data-testid={`ai-text-${key.replace(/_/g, "-")}`}
                />
              </div>
            ))}

            <label className="metadata mt-1 block">XƏTA HALINDA MƏTN</label>
            <textarea
              className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1 font-mono text-xs leading-relaxed"
              rows={2}
              value={ai.fallback_error_text}
              onChange={(e) => setAi((p) => ({ ...p, fallback_error_text: e.target.value }))}
              placeholder={AI_CHAT_DEFAULTS.fallback_error_text}
              data-testid="ai-text-fallback-error"
            />
          </div>

          {/* SUGGESTED PROMPTS — full-width row beneath */}
          <div className="border border-[var(--border)] p-5 md:col-span-2" data-testid="ai-prompts-section">
            <div className="flex items-center justify-between mb-3">
              <div className="metadata text-[var(--text-muted)]">
                XOŞ GƏLDİN EKRANINDAKI SUAL DÜYMƏLƏRİ ({(ai.suggested_prompts || []).length})
              </div>
              <button type="button" onClick={addPrompt} className="btn-outline" data-testid="ai-add-prompt">
                <Plus size={13} /> ƏLAVƏ ET
              </button>
            </div>

            <div className="space-y-2">
              {(ai.suggested_prompts || []).map((p, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <input
                    type="text"
                    className="flex-1 bg-transparent border border-[var(--border)] px-3 py-2 font-mono text-xs"
                    value={p}
                    onChange={(e) => updatePrompt(i, e.target.value)}
                    placeholder="məs. Klassik vinyl tövsiyə et"
                    data-testid={`ai-prompt-${i}`}
                  />
                  <button
                    type="button"
                    onClick={() => removePrompt(i)}
                    className="p-2 text-[var(--accent)] hover:opacity-70"
                    aria-label="Sil"
                    data-testid={`ai-prompt-remove-${i}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
              {(ai.suggested_prompts || []).length === 0 && (
                <div className="text-xs text-[var(--text-muted)] py-3">
                  Sual düyməsi yoxdur — defolt 3 sual göstəriləcək.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Circular cropper modal */}
        <CircularImageCropper
          open={cropperOpen}
          file={cropperFile}
          outputSize={384}
          onCancel={onCropCancel}
          onConfirm={onCropConfirm}
        />
      </div>
      </>
      )}
    </div>
  );
}
